/**
 * Privacy-focused logging utility
 * Controls what information gets logged to the console based on environment
 * and implements redaction for sensitive data
 */

type LogLevel = 'ERROR' | 'WARN' | 'INFO' | 'DEBUG';

// Default to INFO in production, DEBUG in development
const DEFAULT_LOG_LEVEL: LogLevel = import.meta.env.PROD ? 'INFO' : 'DEBUG';

// Get configured log level or use default
const LOG_LEVEL = (import.meta.env.VITE_LOG_LEVEL as LogLevel) || DEFAULT_LOG_LEVEL;

// Is production environment
const IS_PRODUCTION = import.meta.env.PROD;

// Map log levels to numeric values for comparison
const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  'ERROR': 0,
  'WARN': 1,
  'INFO': 2,
  'DEBUG': 3
};

// Check if a given log level should be shown based on the configured level
const shouldLog = (level: LogLevel): boolean => {
  return LOG_LEVEL_PRIORITY[level] <= LOG_LEVEL_PRIORITY[LOG_LEVEL];
};

/**
 * Redacts sensitive IDs by keeping only the first and last few characters
 * @param id The ID to redact
 * @returns A redacted version of the ID
 */
const redactId = (id: string | null | undefined): string => {
  if (!id) return 'null';
  if (id.length <= 8) return '****'; // If very short, just use asterisks
  return `${id.substring(0, 4)}...${id.substring(id.length - 4)}`;
};

/**
 * Sanitizes an object to remove or redact sensitive information
 * @param obj The object to sanitize
 * @returns a sanitized copy of the object
 */
const sanitizeObject = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return obj;
  
  // Create a sanitized copy
  const sanitized: any = Array.isArray(obj) ? [] : {};
  
  // Enhanced sanitization in production environments
  const sensitiveFieldNames = IS_PRODUCTION ? 
    ['password', 'token', 'apiKey', 'access_token', 'refresh_token', 'key', 
     'secret', 'email', 'phone', 'address', 'dob', 'ssn', 'ip'] : 
    ['password', 'token', 'apiKey', 'access_token', 'refresh_token', 'key'];
  
  Object.entries(obj).forEach(([key, value]) => {
    // Redact sensitive field names
    if (sensitiveFieldNames.some(field => key.toLowerCase().includes(field.toLowerCase()))) {
      sanitized[key] = '[REDACTED]';
    }
    // Redact IDs in certain fields
    else if (['id', 'userId', 'user_id', 'sessionId', 'session_id'].includes(key) && typeof value === 'string') {
      sanitized[key] = redactId(value as string);
    }
    // Truncate content fields more aggressively in production
    else if (['content', 'message', 'text', 'body', 'description'].includes(key) && typeof value === 'string') {
      const maxLength = IS_PRODUCTION ? 30 : 50;
      const valueStr = value as string;
      if (valueStr.length > maxLength) {
        sanitized[key] = `${valueStr.substring(0, maxLength)}... [${valueStr.length} chars]`;
      } else {
        sanitized[key] = valueStr;
      }
    }
    // Recursively sanitize nested objects
    else if (value && typeof value === 'object') {
      sanitized[key] = sanitizeObject(value);
    }
    // Otherwise, keep the value as is
    else {
      sanitized[key] = value;
    }
  });
  
  return sanitized;
};

/**
 * Strip error stack traces in production
 * @param error The error to sanitize
 * @returns A sanitized error object
 */
const sanitizeError = (error: any): any => {
  if (!error) return error;
  
  // If it's not an object, return as is
  if (typeof error !== 'object') return error;
  
  // Clone the error to avoid modifying the original
  const sanitized = { ...error };
  
  // Remove stack trace in production
  if (IS_PRODUCTION && sanitized.stack) {
    delete sanitized.stack;
  }
  
  // If it has a message property, keep only that in production
  if (IS_PRODUCTION && sanitized.message) {
    return { message: sanitized.message };
  }
  
  return sanitized;
};

/**
 * Error logger
 * Always logs regardless of configured level
 */
export const logError = (message: string, error?: any): void => {
  if (error) {
    const sanitizedError = IS_PRODUCTION ? 
      sanitizeError(error) : 
      sanitizeObject(error);
    console.error(`ERROR: ${message}`, sanitizedError);
  } else {
    console.error(`ERROR: ${message}`);
  }
};

/**
 * Warning logger
 * Logs when level is WARN or higher
 */
export const logWarn = (message: string, data?: any): void => {
  if (shouldLog('WARN')) {
    if (data) {
      const sanitizedData = sanitizeObject(data);
      console.warn(`WARN: ${message}`, sanitizedData);
    } else {
      console.warn(`WARN: ${message}`);
    }
  }
};

/**
 * Info logger
 * Logs when level is INFO or higher
 */
export const logInfo = (message: string, data?: any): void => {
  if (shouldLog('INFO')) {
    if (data) {
      const sanitizedData = sanitizeObject(data);
      console.log(`INFO: ${message}`, sanitizedData);
    } else {
      console.log(`INFO: ${message}`);
    }
  }
};

/**
 * Debug logger
 * Only logs when level is DEBUG
 */
export const logDebug = (message: string, data?: any): void => {
  if (shouldLog('DEBUG')) {
    if (data) {
      const sanitizedData = sanitizeObject(data);
      console.log(`DEBUG: ${message}`, sanitizedData);
    } else {
      console.log(`DEBUG: ${message}`);
    }
  }
};

// Export a default logging object for convenience
export default {
  error: logError,
  warn: logWarn,
  info: logInfo,
  debug: logDebug,
  redactId,
  sanitizeObject,
  sanitizeError,
  isProduction: IS_PRODUCTION
};
